import { z } from 'zod';
import { LocaleSchema } from './specialization';

/**
 * AI crisis-detection contracts (Module 8).
 *
 * Deliberately narrow: this is NOT a chat-companion feature. It backs a
 * safety layer that screens text the user already typed (a chat message,
 * or an optional pre-booking "what's on your mind" intake note) for
 * self-harm / crisis risk signals, and opens a flag for a human
 * (therapist/admin) to review — see services/api/src/modules/ai/. The AI
 * never replies to the user with therapeutic content and never decides
 * what happens next on its own.
 */

export const AIRiskLevelSchema = z.enum(['LOW', 'MEDIUM', 'HIGH']);
export type AIRiskLevel = z.infer<typeof AIRiskLevelSchema>;

export const AICrisisSourceSchema = z.enum(['CHAT_MESSAGE', 'INTAKE']);
export type AICrisisSource = z.infer<typeof AICrisisSourceSchema>;

export const AICrisisFlagStatusSchema = z.enum(['OPEN', 'ACKNOWLEDGED', 'RESOLVED']);
export type AICrisisFlagStatus = z.infer<typeof AICrisisFlagStatusSchema>;

export const CrisisResourceSchema = z.object({
  name: z.string(),
  phone: z.string(),
  description: z.string(),
});
export type CrisisResource = z.infer<typeof CrisisResourceSchema>;

/** POST /api/v1/ai/intake-check — client-initiated, optional pre-booking screen. */
export const IntakeCheckRequestSchema = z.object({
  text: z.string().min(1).max(4000),
  /** UI locale for `disclaimer`/`crisisResources` (ADR-015 §10) — the analyzer itself screens both languages regardless of this value. */
  locale: LocaleSchema.default('el'),
});
export type IntakeCheckRequest = z.infer<typeof IntakeCheckRequestSchema>;

/**
 * `disclaimer` and `crisisResources` are always present, even when
 * `riskDetected` is false — the "hard disclaimer" guardrail is a
 * standing footer in the client UI, not something shown only reactively.
 */
export const IntakeCheckResponseSchema = z.object({
  riskDetected: z.boolean(),
  riskLevel: AIRiskLevelSchema,
  disclaimer: z.string(),
  crisisResources: z.array(CrisisResourceSchema),
});
export type IntakeCheckResponse = z.infer<typeof IntakeCheckResponseSchema>;

/** GET /api/v1/ai/flags — THERAPIST sees flags on their own bookings, ADMIN sees all. No excerpt here. */
export const AICrisisFlagSummarySchema = z.object({
  id: z.string().cuid(),
  source: AICrisisSourceSchema,
  riskLevel: AIRiskLevelSchema,
  status: AICrisisFlagStatusSchema,
  bookingId: z.string().cuid().nullable(),
  createdAt: z.string().datetime(),
  acknowledgedAt: z.string().datetime().nullable(),
});
export type AICrisisFlagSummary = z.infer<typeof AICrisisFlagSummarySchema>;

export const ListCrisisFlagsResponseSchema = z.object({
  flags: z.array(AICrisisFlagSummarySchema),
});
export type ListCrisisFlagsResponse = z.infer<typeof ListCrisisFlagsResponseSchema>;

/**
 * GET /api/v1/ai/flags/:id — decrypts and returns the flagged excerpt.
 * Same "reveal is an explicit, audited action" pattern as the
 * emergency-contact reveal (AuditLog(AI_CRISIS_FLAG_ACKNOWLEDGED) is
 * written server-side); also marks the flag ACKNOWLEDGED.
 */
export const AICrisisFlagDetailSchema = AICrisisFlagSummarySchema.extend({
  excerpt: z.string(),
});
export type AICrisisFlagDetail = z.infer<typeof AICrisisFlagDetailSchema>;
